// src/services/vendorChat/websocket.ts
import { QueryClient } from 'react-query';
import TokenService from '../service.token';
import { IChat, IMessage, IUser, IChatListResponse, IShopImage } from './types';

type MessageHandler = (msg: IMessage) => void;
type ChatListHandler = (chats: IChat[]) => void;

const extractIdFromEmail = (email: string): number => {
  const match = email.match(/(\d+)/);
  return match ? parseInt(match[0], 10) : Date.now();
};

interface WsRawMessage {
  message_id?: string | number;
  id?: string | number;
  chat_id?: string | number;
  chat?: string | number;
  sender: Partial<IUser> & { email: string };
  receiver: Partial<IUser> & { email: string };
  message_type?: string;
  message?: string;
  content?: string;
  image?: string | null;
  image_small?: string | null;
  image_medium?: string | null;
  audio?: string | null;
  video?: string | null;
  is_read?: boolean;
  created_at?: string;
  updated_at?: string;
}

const mapWsMessageToIMessage = (msg: WsRawMessage): IMessage => ({
  id: String(msg.message_id ?? msg.id),
  chat: parseInt(String(msg.chat_id ?? msg.chat), 10),
  sender: {
    id: msg.sender.id ?? extractIdFromEmail(msg.sender.email),
    email: msg.sender.email,
    first_name: msg.sender.first_name ?? '',
    last_name: msg.sender.last_name ?? '',
    photo: msg.sender.photo ?? null,
    photo_small: msg.sender.photo_small ?? null,
    photo_medium: msg.sender.photo_medium ?? null,
    user_type: msg.sender.user_type,
    address: msg.sender.address ?? null,
    gender: msg.sender.gender ?? null,
    country: msg.sender.country ?? null,
    created_at: msg.sender.created_at ?? new Date().toISOString(),
  },
  receiver: {
    id: msg.receiver.id ?? extractIdFromEmail(msg.receiver.email),
    email: msg.receiver.email,
    first_name: msg.receiver.first_name ?? '',
    last_name: msg.receiver.last_name ?? '',
    photo: msg.receiver.photo ?? null,
    photo_small: msg.receiver.photo_small ?? null,
    photo_medium: msg.receiver.photo_medium ?? null,
    user_type: msg.receiver.user_type,
    address: msg.receiver.address ?? null,
    gender: msg.receiver.gender ?? null,
    country: msg.receiver.country ?? null,
    created_at: msg.receiver.created_at ?? new Date().toISOString(),
  },
  message_type: (msg.message_type as IMessage['message_type']) ?? 'text',
  content: msg.message ?? msg.content ?? '',
  image: msg.image ?? null,
  image_small: msg.image_small ?? null,
  image_medium: msg.image_medium ?? null,
  audio: msg.audio ?? null,
  video: msg.video ?? null,
  is_read: !!msg.is_read,
  created_at: msg.created_at ?? new Date().toISOString(),
  updated_at: msg.updated_at ?? msg.created_at ?? new Date().toISOString(),
});

class VendorChatWebSocket {
  private globalWs: WebSocket | null = null;
  private chatWsMap = new Map<number, WebSocket>();
  private globalHandler: ChatListHandler | null = null;
  private chatHandlers = new Map<number, MessageHandler>();
  private queryClient: QueryClient | null = null;
  private globalReconnectTimer: NodeJS.Timeout | null = null;
  private chatReconnectTimers = new Map<number, NodeJS.Timeout>();
  private isGlobalConnecting = false;
  private connectingChats = new Set<number>();

  connectGlobal(onUpdate: ChatListHandler, queryClient: QueryClient) {
    this.globalHandler = onUpdate;
    this.queryClient = queryClient;

    const token = TokenService.getToken()?.access;
    if (!token) {
      console.warn('No token available for WebSocket connection');
      return;
    }

    // Prevent multiple simultaneous connection attempts
    if (this.isGlobalConnecting) {
      return;
    }

    if (
      this.globalWs?.readyState === WebSocket.OPEN ||
      this.globalWs?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    // Clear any pending reconnection
    if (this.globalReconnectTimer) {
      clearTimeout(this.globalReconnectTimer);
      this.globalReconnectTimer = null;
    }

    this.isGlobalConnecting = true;
    const url = `wss://api.kurtasari.com/v1/ws/chats/?token=${token}`;
    this.globalWs = new WebSocket(url);

    this.globalWs.onopen = () => {
      this.isGlobalConnecting = false;
      // console.log('Global chat WebSocket connected');
    };

    this.globalWs.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as {
          action: string;
          messages?: WsRawMessage[];
        };

        if (data.action === 'load_messages' && Array.isArray(data.messages)) {
          const chatMap = new Map<number, IChat>();

          data.messages.forEach((msg) => {
            const chatId = parseInt(String(msg.chat_id), 10);
            if (!chatMap.has(chatId)) {
              chatMap.set(chatId, {
                id: chatId,
                vendor: {
                  user_id: 0,
                  company_name: '',
                  user: {} as IUser,
                  shop_image: {} as IShopImage,
                },
                user: {} as IUser,
                messages: [],
                created_at: '',
                updated_at: msg.created_at ?? '',
              });
            }
            chatMap.get(chatId)!.messages.push(mapWsMessageToIMessage(msg));
          });

          const updatedChats: IChat[] = Array.from(chatMap.values()).map((chat) => ({
            ...chat,
            messages: chat.messages.sort((a, b) => a.created_at.localeCompare(b.created_at)),
          }));

          this.globalHandler?.(updatedChats);
        } else if (data.action === 'new_message' && data.messages?.[0]) {
          const msg = data.messages[0];
          const chatId = parseInt(String(msg.chat_id), 10);
          const newMessage = mapWsMessageToIMessage(msg);

          queryClient.invalidateQueries('vendor-chats');

          queryClient.setQueriesData<IChatListResponse>(['vendor-chats'], (old) => {
            if (!old) return { count: 0, next: null, previous: null, results: [] };

            return {
              ...old,
              results: old.results
                .map((chat) => {
                  if (chat.id === chatId) {
                    const messageExists = chat.messages.some((m) => m.id === newMessage.id);

                    return {
                      ...chat,
                      updated_at: newMessage.created_at,
                      messages: messageExists ? chat.messages : [...chat.messages, newMessage],
                    };
                  }
                  return chat;
                })
                .sort((a, b) => {
                  return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
                }),
            };
          });
        } else if (data.action === 'message_read' && data.messages?.[0]) {
          const msg = data.messages[0];
          const chatId = parseInt(String(msg.chat_id), 10);
          const messageId = String(msg.message_id ?? msg.id);

          queryClient.setQueriesData<IChatListResponse>(['vendor-chats'], (old) => {
            if (!old) return { count: 0, next: null, previous: null, results: [] };

            return {
              ...old,
              results: old.results.map((chat) => {
                if (chat.id === chatId) {
                  return {
                    ...chat,
                    messages: chat.messages.map((m) =>
                      m.id === messageId ? { ...m, is_read: true } : m,
                    ),
                  };
                }
                return chat;
              }),
            };
          });

          queryClient.setQueryData<IChat | undefined>(['vendor-chat', chatId], (old) => {
            if (!old) return old;
            return {
              ...old,
              messages: old.messages.map((m) => (m.id === messageId ? { ...m, is_read: true } : m)),
            };
          });
        } else if (data.action === 'unread_message' && Array.isArray(data.messages)) {
          data.messages.forEach((msg) => {
            const chatId = parseInt(String(msg.chat_id), 10);
            const messageId = String(msg.message_id ?? msg.id);
            const fullMsg = mapWsMessageToIMessage(msg);

            queryClient.setQueriesData<IChatListResponse>(['vendor-chats'], (old) => {
              if (!old) return { count: 0, next: null, previous: null, results: [] };
              return {
                ...old,
                results: old.results.map((chat) => {
                  if (chat.id === chatId) {
                    const exists = chat.messages.some((m) => m.id === fullMsg.id);
                    return {
                      ...chat,
                      updated_at: fullMsg.created_at,
                      messages: exists ? chat.messages : [...chat.messages, fullMsg],
                    };
                  }
                  return chat;
                }),
              };
            });

            queryClient.setQueryData<IChat | undefined>(['vendor-chat', chatId], (old) => {
              if (!old) return old;
              return {
                ...old,
                messages: old.messages.map((m) =>
                  m.id === messageId ? { ...m, is_read: true } : m,
                ),
              };
            });

            const ws = this.chatWsMap.get(chatId);
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(
                JSON.stringify({
                  action: 'read_message',
                  message_id: messageId,
                }),
              );
            }
          });
        }
      } catch (err) {
        console.error('Error parsing global chat WS message:', err);
      }
    };

    this.globalWs.onerror = (err) => {
      this.isGlobalConnecting = false;
      console.error('Global chat WebSocket error:', err);
    };

    this.globalWs.onclose = () => {
      this.isGlobalConnecting = false;
      this.globalWs = null;

      // console.log('Global chat WebSocket closed', event.code, event.reason);

      // Only reconnect if handlers are still set (component not unmounted)
      if (this.globalHandler && this.queryClient) {
        // Clear any existing timer
        if (this.globalReconnectTimer) {
          clearTimeout(this.globalReconnectTimer);
        }

        // Reconnect after delay
        this.globalReconnectTimer = setTimeout(() => {
          this.globalReconnectTimer = null;
          if (this.globalHandler && this.queryClient) {
            this.connectGlobal(onUpdate, queryClient);
          }
        }, 3000);
      }
    };
  }

  connectChat(chatId: number, onMessage: MessageHandler, queryClient: QueryClient) {
    const token = TokenService.getToken()?.access;
    if (!token) {
      console.warn('No token available for chat WebSocket connection');
      return;
    }

    // Prevent multiple simultaneous connection attempts for same chat
    if (this.connectingChats.has(chatId)) {
      return;
    }

    const existing = this.chatWsMap.get(chatId);
    if (existing?.readyState === WebSocket.OPEN || existing?.readyState === WebSocket.CONNECTING) {
      this.chatHandlers.set(chatId, onMessage);
      return;
    }

    // Clear any pending reconnection for this chat
    const reconnectTimer = this.chatReconnectTimers.get(chatId);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      this.chatReconnectTimers.delete(chatId);
    }

    this.connectingChats.add(chatId);
    const url = `wss://api.kurtasari.com/v1/ws/chat/${chatId}/?token=${token}`;
    const ws = new WebSocket(url);

    ws.onopen = () => {
      this.connectingChats.delete(chatId);
      this.chatWsMap.set(chatId, ws);
      this.chatHandlers.set(chatId, onMessage);
      console.log(`Chat ${chatId} WebSocket connected`);
    };

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as {
          action: string;
          messages?: WsRawMessage[];
          id?: string | number;
          message_id?: string | number;
          chat?: string | number;
          chat_id?: string | number;
        };

        if (data.action === 'new_message' && Array.isArray(data.messages)) {
          const msg = data.messages[0];
          if (msg?.chat_id && msg?.message_id) {
            const fullMsg = mapWsMessageToIMessage(msg);
            this.chatHandlers.get(fullMsg.chat)?.(fullMsg);

            if (this.queryClient) {
              this.queryClient.setQueriesData<IChatListResponse>(['vendor-chats'], (old) => {
                if (!old) return { count: 0, next: null, previous: null, results: [] };

                return {
                  ...old,
                  results: old.results
                    .map((chat) => {
                      if (chat.id === fullMsg.chat) {
                        const messageExists = chat.messages.some((m) => m.id === fullMsg.id);
                        return {
                          ...chat,
                          updated_at: fullMsg.created_at,
                          messages: messageExists ? chat.messages : [...chat.messages, fullMsg],
                        };
                      }
                      return chat;
                    })
                    .sort((a, b) => {
                      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
                    }),
                };
              });
            }
          }
        } else if (data.action === 'load_messages' && Array.isArray(data.messages)) {
          data.messages.forEach((msg) => {
            if (msg?.message_id && msg?.chat_id) {
              const fullMsg = mapWsMessageToIMessage(msg);
              this.chatHandlers.get(parseInt(String(msg.chat_id), 10))?.(fullMsg);
            }
          });
        } else if (data.action === 'message_read' && Array.isArray(data.messages)) {
          const msg = data.messages[0];
          if (msg?.message_id && msg?.chat_id) {
            const chatId = parseInt(String(msg.chat_id), 10);
            const messageId = String(msg.message_id);

            queryClient.setQueryData<IChat | undefined>(['vendor-chat', chatId], (old) => {
              if (!old) return old;
              return {
                ...old,
                messages: old.messages.map((m) =>
                  m.id === messageId ? { ...m, is_read: true } : m,
                ),
              };
            });
          }
        } else if (data.action === 'unread_message' && Array.isArray(data.messages)) {
          data.messages.forEach((msg) => {
            const chatId = parseInt(String(msg.chat_id), 10);
            const messageId = String(msg.message_id ?? msg.id);
            const fullMsg = mapWsMessageToIMessage(msg);

            this.chatHandlers.get(chatId)?.(fullMsg);

            queryClient.setQueryData<IChat | undefined>(['vendor-chat', chatId], (old) => {
              if (!old) return old;
              return {
                ...old,
                messages: old.messages.map((m) =>
                  m.id === messageId ? { ...m, is_read: true } : m,
                ),
              };
            });

            if (ws.readyState === WebSocket.OPEN) {
              ws.send(
                JSON.stringify({
                  action: 'read_message',
                  message_id: messageId,
                }),
              );
            }
          });
        } else if (data.id || data.message_id) {
          const chatId = data.chat ?? data.chat_id;
          if (chatId && 'sender' in data && 'receiver' in data) {
            const fullMsg = mapWsMessageToIMessage(data as WsRawMessage);
            this.chatHandlers.get(parseInt(String(chatId), 10))?.(fullMsg);
          }
        }
      } catch (err) {
        console.error('Error parsing chat WS message:', err);
      }
    };

    ws.onerror = (err) => {
      this.connectingChats.delete(chatId);
      console.error(`Chat ${chatId} WebSocket error:`, err);
    };

    ws.onclose = (event) => {
      this.connectingChats.delete(chatId);
      this.chatWsMap.delete(chatId);
      const handler = this.chatHandlers.get(chatId);
      this.chatHandlers.delete(chatId);

      console.log(`Chat ${chatId} WebSocket closed`, event.code, event.reason);

      // Only reconnect if handler still exists (component not unmounted)
      if (handler) {
        // Clear any existing timer
        const existingTimer = this.chatReconnectTimers.get(chatId);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }

        // Reconnect after delay
        const timer = setTimeout(() => {
          this.chatReconnectTimers.delete(chatId);
          if (this.chatHandlers.has(chatId) || handler) {
            this.connectChat(chatId, handler, queryClient);
          }
        }, 3000);

        this.chatReconnectTimers.set(chatId, timer);
      }
    };
  }

  sendMessage(chatId: number, content: string, receiverId: number) {
    const ws = this.chatWsMap.get(chatId);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn(`WebSocket for chat ${chatId} is not open`);
      return;
    }

    ws.send(
      JSON.stringify({
        action: 'send_message',
        content: content.trim(),
        media_type: 'text',
        receiver_id: receiverId,
      }),
    );
  }

  markMessageAsRead(chatId: number, messageId: string) {
    const ws = this.chatWsMap.get(chatId);
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn(`WebSocket for chat ${chatId} is not open`);
      return;
    }

    ws.send(
      JSON.stringify({
        action: 'read_message',
        message_id: messageId,
      }),
    );
  }

  disconnectChat(chatId: number) {
    // Clear reconnect timer
    const timer = this.chatReconnectTimers.get(chatId);
    if (timer) {
      clearTimeout(timer);
      this.chatReconnectTimers.delete(chatId);
    }

    this.connectingChats.delete(chatId);

    // Close WebSocket if it exists and is in a closeable state
    const ws = this.chatWsMap.get(chatId);
    if (ws) {
      const readyState = ws.readyState;
      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    }

    this.chatWsMap.delete(chatId);
    this.chatHandlers.delete(chatId);
  }

  disconnectGlobal() {
    // Clear reconnect timer
    if (this.globalReconnectTimer) {
      clearTimeout(this.globalReconnectTimer);
      this.globalReconnectTimer = null;
    }

    this.isGlobalConnecting = false;

    // Close WebSocket if it exists and is in a closeable state
    if (this.globalWs) {
      const readyState = this.globalWs.readyState;
      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
        this.globalWs.close();
      }
      this.globalWs = null;
    }

    this.globalHandler = null;
    this.queryClient = null;
  }

  disconnectAll() {
    this.disconnectGlobal();
    for (const id of this.chatWsMap.keys()) {
      this.disconnectChat(id);
    }
  }
}

export const vendorChatWebSocket = new VendorChatWebSocket();
